import Vue from "vue";

// Composition API
import VueCompositionApi from "@vue/composition-api";

Vue.use(VueCompositionApi);

// VueResource
import VueResource from "vue-resource";

Vue.use(VueResource);

// i18n
import i18n from "./plugins/i18n";

// vuetify-dialog
import VuetifyDialog from "vuetify-dialog";
import "vuetify-dialog/dist/vuetify-dialog.css";

Vue.use(VuetifyDialog, {
  context: {
    i18n,
    vuetify,
    store,
  },
  confirm: {
    showClose: false,
  },
});

// vue-virtual-scroller
import VueVirtualScroller from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";

Vue.use(VueVirtualScroller);

import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import _ from "lodash";
import { DateTime } from "luxon";
import { ACTION_TYPES } from "@/store/types";
import { PERMISSIONS } from "@/constants";
import PdfViewer from "@/components/PdfViewer";
import VueToast from "vue-toast-notification";
import "vue-toast-notification/dist/theme-bootstrap.css";
import floatingPointFix from "js-floating-point";
Vue.use(VueToast);
Vue.config.productionTip = false;

export const HOST = "/api/";

Vue.use({
  install() {
    //Event Hub
    Vue.prototype.$eventHub = new Vue();

    // Copy to Clipboard
    Vue.prototype.$copyToClipboard = async function (text) {
      let v = this;

      async function fallbackCopyTextToClipboard(text) {
        let textArea = document.createElement("textarea");
        textArea.value = text;

        // Avoid scrolling to bottom
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.position = "fixed";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          let successful = document.execCommand("copy");
          let msg = successful ? "successful" : "unsuccessful";
          console.log("Fallback: Copying text command was " + msg);
        } catch (err) {
          console.error("Fallback: Oops, unable to copy", err);
        }

        document.body.removeChild(textArea);

        v.$showAlertMessage("Copied!");
      }

      if (!navigator.clipboard) {
        await fallbackCopyTextToClipboard(text);
        return;
      }

      try {
        await navigator.clipboard.writeText(text);
        this.$showAlertMessage("Copied!");
        console.log("Async: Copying to clipboard was successful!");
      } catch (err) {
        console.error("Async: Could not copy text: ", err);
      }
    };

    // Filtering
    Vue.prototype.$filterItems = function (items, filters) {
      let filteringAttrs = Object.keys(filters);

      if (filteringAttrs) {
        filteringAttrs.forEach((path) => {
          let filterValue = filters[path];

          if (filterValue instanceof Array) {
            items = _.filter(items, (i) => {
              return _.some(
                filters[path].map((v) => {
                  return _.get(i, path) === v;
                })
              );
            });
          } else if (filterValue instanceof Object) {
            if (filterValue.date_range) {
              let dateRange = filterValue.value;
              let fromDate = new Date(dateRange[0]);
              let toDate = new Date(dateRange[1]);
              items = _.filter(items, (i) => {
                let recordValue = new Date(_.get(i, path));

                return fromDate <= recordValue && recordValue <= toDate;
              });
            }
          } else if (
            typeof filterValue === "string" ||
            filterValue instanceof String
          ) {
            items = _.filter(items, (i) => {
              return (
                _.get(i, path) &&
                _.get(i, path)
                  .toLowerCase()
                  .indexOf(filterValue.toLowerCase()) !== -1
              );
            });
          } else if (
            typeof filterValue === "boolean" ||
            filterValue instanceof Boolean
          ) {
            items = _.filter(items, (i) => {
              return Boolean(_.get(i, path)) === filterValue;
            });
          }
        });
      }

      return items;
    };

    // API Call
    Vue.prototype.$downloadFile = async function (file_id) {
      let path = `file/${file_id}`;

      let response = await this.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: path,
        params: {},
        opt: { extra: { responseType: "arraybuffer" } },
      });

      let blob = new Blob([response.data], {
        type: response.headers.get("content-type"),
      });
      let link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = decodeURI(response.headers.get("content-filename"));
      link.click();
    };

    Vue.prototype.$showAlertMessage = function (
      message,
      color = "",
      icon = ""
    ) {
      this.$store.dispatch(ACTION_TYPES.SHOW_SNACKBAR, {
        message,
        color,
        icon,
      });
    };

    Vue.prototype.$roundNumber = function (v, rounding) {
      let r = parseFloat(rounding);
      return Math.round(floatingPointFix(v / r)) * r;
    };

    Vue.prototype.$parseCurrency = function (v, rounding) {
      let parsed = floatingPointFix(v);
      if (rounding) {
        parsed = this.$roundNumber(v, rounding);
      }
      return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(parsed);
    };
    Vue.prototype.$parseNumber = function (v, digits = 2) {
      return Intl.NumberFormat("en-US", {
        minimumFractionDigits: digits,
      }).format(v);
    };
    Vue.prototype.$parsePeriod = function (v) {
      try {
        return DateTime.fromFormat(v, "yyyyMM").toFormat("yyyy/MMM");
      } catch (e) {
        return "-";
      }
    };

    Vue.prototype.$parseDate = function (d, fmt = "yyyy-MM-dd HH:mm") {
      return DateTime.fromISO(d).toFormat(fmt);
    };

    Vue.prototype.$parseFileSize = function (bytes, si = false, dp = 1) {
      const thresh = si ? 1000 : 1024;

      if (Math.abs(bytes) < thresh) {
        return bytes + " B";
      }

      const units = si
        ? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
        : ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
      let u = -1;
      const r = 10 ** dp;

      do {
        bytes /= thresh;
        ++u;
      } while (
        Math.round(Math.abs(bytes) * r) / r >= thresh &&
        u < units.length - 1
      );

      return bytes.toFixed(dp) + " " + units[u];
    };

    Vue.prototype.$convertFileToBase64 = async function (file) {
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });

      return await toBase64(file);
    };

    Vue.prototype.$me = function () {
      return this.$store.getters.account;
    };

    Vue.prototype.$showPdf = async function (src, pageNo) {
      await this.$dialog.showAndWait(PdfViewer, {
        showClose: false,
        scrollable: true,
        width: "85vw",

        src,
        pageNo,
      });
    };

    //
    Vue.prototype.$randomRgb = function (alpha) {
      var o = Math.round,
        r = Math.random,
        s = 255;
      return (
        "rgba(" +
        o(r() * s) +
        "," +
        o(r() * s) +
        "," +
        o(r() * s) +
        "," +
        alpha +
        ")"
      );
    };

    Vue.prototype.$processedUnit = function (unit) {
      return _.reduce(
        [
          { pattern: /(Per linear centimetre)/i, replacement: "cm" },
          { pattern: /(Each)/i, replacement: "Each" },
          { pattern: /(Each or per leaf)/i, replacement: "Each" },
          { pattern: /(Each sprinkler system)/i, replacement: "Each" },
          { pattern: /(Per hour)/i, replacement: "Hour" },
          { pattern: /(Per item)/i, replacement: "Item" },
          { pattern: /(Per kg)/i, replacement: "kg" },
          { pattern: /(Per litre)/i, replacement: "L" },
          { pattern: /(Per litres)/i, replacement: "L" },
          { pattern: /(Per linear meter)/i, replacement: "m" },
          { pattern: /(Per linear metre)/i, replacement: "m" },
          { pattern: /(Per squaere meter)/i, replacement: "m²" },
          { pattern: /(Per square meter)/i, replacement: "m²" },
          { pattern: /(Per square metre)/i, replacement: "m²" },
          { pattern: /(Square metre per month)/i, replacement: "m²/month" },
          { pattern: /(Per cubic metre)/i, replacement: "m³" },
          { pattern: /(Per month)/i, replacement: "month" },
          { pattern: /(Per number)/i, replacement: "No." },
          { pattern: /(Number per month)/i, replacement: "No./month" },
          { pattern: /(Number Per Works Order)/i, replacement: "No./W.O." },
          { pattern: /(Pair)/i, replacement: "pair" },
          { pattern: /(Per pair)/i, replacement: "pair" },
          { pattern: /("Per 1,000")/i, replacement: "Per 1,000" },
          { pattern: /(Per 100)/i, replacement: "Per 100" },
          { pattern: /(Per 100 kg)/i, replacement: "Per 100 kg" },
          { pattern: /(Per chimney)/i, replacement: "chimney" },
          { pattern: /(Per circuit)/i, replacement: "circuit" },
          { pattern: /(Per crane)/i, replacement: "crane" },
          { pattern: /(Per hoist)/i, replacement: "hoist" },
          { pattern: /(Per inspection)/i, replacement: "inspection" },
          { pattern: /(Per installation)/i, replacement: "installation" },
          { pattern: /(Per item per Works Order)/i, replacement: "item/W.O." },
          { pattern: /(Per lot)/i, replacement: "lot" },
          {
            pattern: /(Per square metre of surface area)/i,
            replacement: "m²(surface area)",
          },
          {
            pattern: /(Per square metre of surface area facing air stream)/i,
            replacement: "m²(surface area facing air stream)",
          },
          { pattern: /(Per cubic metre per hour)/i, replacement: "m³/hour" },
          {
            pattern: /(Per number of site safety model worker)/i,
            replacement: "No.",
          },
          { pattern: /(Per number-month)/i, replacement: "No.-month" },
          { pattern: /(Per number-tree)/i, replacement: "No.-tree" },
          { pattern: /(Per number-week)/i, replacement: "No.-week" },
          { pattern: /(Per piece)/i, replacement: "piece" },
          { pattern: /(Per pipeline)/i, replacement: "pipeline" },
          { pattern: /(Per strainer)/i, replacement: "strainer" },
          { pattern: /(Per switch)/i, replacement: "switch" },
          { pattern: /(Per system)/i, replacement: "system" },
          { pattern: /(Per tank)/i, replacement: "tank" },
          { pattern: /(Per valve)/i, replacement: "valve" },
          { pattern: /(Per way)/i, replacement: "way" },
          { pattern: /(Per winch)/i, replacement: "winch" },
          { pattern: /(Per point)/i, replacement: "point" },
          { pattern: /(Per set)/i, replacement: "set" },
          { pattern: /(Per trip)/i, replacement: "trip" },
        ],
        (u, { pattern, replacement }) => {
          return u.replace(pattern, replacement);
        },
        unit
      );
    };

    // TeamNote OAuth
    Vue.prototype.$loginTeamNote = async function (path) {
      const response = await store.dispatch(ACTION_TYPES.CALL_API, {
        url: "teamnote_oauth/",
      });
      const host = response.body.host;
      const clientId = response.body.client_id;
      const preferredMethod = response.body.preferred_method;

      setTimeout(() => {
        let afterLogin =
          window.location.origin +
          window.location.pathname +
          (path ? `#${path}` : window.location.hash);
        window.location.href = `${host}/oauth2/auth?response_type=id_token&client_id=${clientId}&scope=user_name&redirect_uri=${encodeURIComponent(
          afterLogin
        )}&prefer=${encodeURIComponent(preferredMethod)}`;
      }, 50);
    };

    // round to 15 digits
    Vue.prototype.$roundTo15Digits = function (number) {
      const sign = Number(number) < 0 ? "-" : "";
      const absNumber = Math.abs(number);
      const numStr = absNumber.toString(); // Convert number to string
      const [integerPart, fractionalPart = ""] = numStr.split("."); // Split into integer and fractional parts

      const integerLength = integerPart.length; // Length of the integer part
      const decimalPlaces = Math.max(15 - integerLength, 0); // Allowed decimal places

      if (decimalPlaces === 0) return parseFloat(integerPart); // No decimals allowed, return integer part

      // Combine integer and fractional part to work as a precise string
      const combined =
        integerPart + fractionalPart.padEnd(decimalPlaces + 1, "0"); // Pad fractional part
      const cutoffIndex = integerLength + decimalPlaces; // Total digits to keep
      const digitAfterCutoff = parseInt(combined[cutoffIndex] || "0", 10); // Digit after the cutoff for rounding

      // Perform rounding (half-up)
      let rounded =
        BigInt(combined.slice(0, cutoffIndex)) +
        (digitAfterCutoff >= 5 ? 1n : 0n);

      // Convert back to string and insert decimal point
      let roundedStr = rounded
        .toString()
        .padStart(integerLength + decimalPlaces, "0"); // Ensure correct length

      const result =
        sign +
        roundedStr.slice(0, integerLength) +
        "." +
        roundedStr.slice(integerLength);

      return parseFloat(result); // Convert back to number
    };
  },
});

let currentRouteName = null;
window.onbeforeunload = function (e) {
  if (
    ["public-dim-book", "work-order-detail"].indexOf(currentRouteName) !== -1
  ) {
    return "Are you sure to quit?";
  }
};

router.beforeEach((to, from, next) => {
  if (["public-dim-book", "work-order-detail"].indexOf(from.name) !== -1) {
    if (!confirm("Are you sure to quit?")) {
      currentRouteName = from.name;
      next(from);
      return;
    }
  }
  
  // Check if route requires permission
  if (to.meta && to.meta.requiresPermission) {
    const account = store.getters.account;
    if (!account) {
      // User not logged in, redirect to login
      next({ name: "landing" });
      return;
    }
    
    // Check if this is an RBU permission
    const permissionKey = to.meta.requiresPermission;
    const isRbuPermission = PERMISSIONS[permissionKey] && PERMISSIONS[permissionKey].startsWith('rbu.');
    
    const hasPermission = store.getters.hasPermission(PERMISSIONS[permissionKey], isRbuPermission);
    if (!hasPermission) {
      // User doesn't have required permission, redirect to landing or show error
      store.dispatch(ACTION_TYPES.SHOW_SNACKBAR, {
        message: "You don't have permission to access this page.",
        type: "error"
      });
      next({ name: "landing" });
      return;
    }
  }
  
  currentRouteName = to.name;
  next();
});

new Vue({
  router,
  store,
  i18n,
  vuetify,
  render: (h) => h(App),
}).$mount("#app");
